import { useState } from 'react';

import { cn } from '@/shadcn/lib/utils';

import { isLockedWidget } from '@/components/forms/utils/links';

import { WidgetWriterProps } from '../widget.type';
import { arraysMatch, deselectValue, selectValue } from './checkboxes.utils';
import { Checkbox } from '@/shadcn/checkbox';
import { TestId } from '@/types/testId';
type CheckState = boolean | 'indeterminate';
type Props = {
  options: {
    enumOptions: any[];
    enumDisabled: any[];
    inline: boolean;
  };
} & WidgetWriterProps;

export const CheckboxesWidget = ({
  disabled,
  options: { enumOptions, enumDisabled = [], inline = false },
  value,
  readonly,
  onChange,
  id,
  name,
  formContext,
  invalid
}: Props) => {
  /**
   * Building an array of string values
   * in order to match with an array of results values.
   */

  const enumOptionsValues: string[] = Object.values(enumOptions)
    .map(e => e.value)
    .filter(option => !enumDisabled.includes(option) || value.includes(option));

  const [tempEnumOptions, setTempEnumOptions] = useState(enumOptionsValues);

  /**
   * Check if the selectMenu option value has changed.
   * If so, we clear the array of results values.
   */
  if (!arraysMatch(enumOptionsValues, tempEnumOptions)) {
    setTempEnumOptions(enumOptionsValues);
    onChange([]);
  }

  const handleSingleCheck = (checkState: CheckState, optionValue: boolean) => {
    const all = enumOptions.map(({ value }) => value);

    if (checkState === true) {
      return onChange(selectValue(optionValue, value, all));
    }
    return onChange(deselectValue(optionValue, value));
  };

  const handleAllChecked = (checkState: CheckState) => {
    if (checkState === false) {
      const disabledOptionsSelected = value.filter(option =>
        enumDisabled.includes(option)
      );
      return onChange(disabledOptionsSelected);
    } else if (checkState === true) {
      return onChange(enumOptionsValues);
    }
  };

  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(id, formContext.links);

  const allDisabled = disabled || readonly || isLocked;
  const isAllCheckDisplay = enumOptions.length > 1;

  const getAllCheckState = (): CheckState => {
    if (arraysMatch(enumOptionsValues, value)) return true;
    if (value.length === 0) return false;
    return 'indeterminate';
  };

  return (
    <div
      id={id}
      className={cn(
        'flex flex-col gap-3',
        inline && 'flex-row items-center justify-between'
      )}
      data-cy={`checkboxes-${name}`}
    >
      {isAllCheckDisplay && (
        <div className="flex items-center space-x-2">
          <Checkbox
            id={`check-all-${id}`}
            disabled={allDisabled}
            checked={getAllCheckState()}
            onCheckedChange={handleAllChecked}
            data-testid={TestId.FormWriter.Checkboxes.CheckBoxesAll}
            aria-invalid={invalid}
            data-cy={`checkboxes-check-all-${name}`}
          />
          <label
            htmlFor={`check-all-${id}`}
            className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            data-testid={TestId.FormWriter.Checkboxes.CheckboxesLabel}
          >
            Select all
          </label>
        </div>
      )}

      {enumOptions.map(({ value: optionValue, label }) => {
        const checked = value.indexOf(optionValue) !== -1;
        const itemDisabled =
          enumDisabled && enumDisabled.indexOf(optionValue) !== -1;
        return (
          <div
            className={cn(
              'flex items-center space-x-2',
              isAllCheckDisplay && 'ml-5'
            )}
            key={`${id}-${optionValue}`}
          >
            <Checkbox
              id={`${id}-${optionValue}`}
              disabled={allDisabled || itemDisabled}
              checked={checked}
              onCheckedChange={e => handleSingleCheck(e, optionValue)}
              data-testid={TestId.FormWriter.Checkboxes.Checkboxes}
              data-cy={`checkboxes-${name}-${optionValue}`}
              aria-invalid={invalid}
            />
            <label
              htmlFor={`${id}-${optionValue}`}
              className="cursor-pointer text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              data-testid={TestId.FormWriter.Checkboxes.CheckboxesLabel}
            >
              {label}
            </label>
          </div>
        );
      })}
    </div>
  );
};
